'use client';

import Link from 'next/link';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ThumbsUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  usePostLikers,
  likerName,
  likerAvatar,
  likerUserId,
  type PostLiker,
} from '@/api/usePostLikers';

interface PostLikersDialogProps {
  postID: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

export default function PostLikersDialog({
  postID,
  isOpen,
  onOpenChange,
}: PostLikersDialogProps) {
  const { data, isLoading, hasNextPage, fetchNextPage } = usePostLikers({
    postID,
    enabled: isOpen,
  });

  const likers: PostLiker[] =
    data?.pages?.flatMap((p) => p.responseObject?.rows || []) || [];
  const total = data?.pages?.[0]?.responseObject?.count ?? likers.length;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col">
        {/* Two things made this look lopsided. DialogHeader's own base is
            `md:w-[80%] md:mx-auto`, so on desktop the header was 80% wide and
            centred while the list below was full width and left-aligned —
            different widths and different left edges. And my earlier `pl-12`
            (to dodge the close button at top-4 left-4) indented it further
            still. So: match the list's width, and clear the button by
            dropping below it rather than beside it. Its bottom edge is 62px;
            content starts at 24px (p-6) / 40px (lg:p-10), hence the two
            margins. */}
        <DialogHeader className="mt-12 lg:mt-8 md:w-full md:mx-0">
          <DialogTitle className="flex items-center gap-2">
            <ThumbsUp aria-hidden="true" className="h-4 w-4" />
            Likes
          </DialogTitle>
          <DialogDescription>
            {isLoading
              ? 'Loading…'
              : `${total} ${total === 1 ? 'person has' : 'people have'} liked this post.`}
          </DialogDescription>
        </DialogHeader>

        <div id="post-likers-scroll" className="flex-1 overflow-y-auto -mx-2 px-2">
          {isLoading ? (
            <ul className="space-y-3 py-2">
              {[0, 1, 2].map((i) => (
                <li key={i} className="flex items-center gap-3">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <Skeleton className="h-4 w-40" />
                </li>
              ))}
            </ul>
          ) : likers.length === 0 ? (
            <p className="py-8 text-center text-sm text-[#554E49]">
              No likes yet.
            </p>
          ) : (
            <InfiniteScroll
              dataLength={likers.length}
              next={fetchNextPage}
              hasMore={!!hasNextPage}
              loader={
                <div className="py-3 text-center text-sm text-[#554E49]">
                  Loading more…
                </div>
              }
              scrollableTarget="post-likers-scroll"
            >
              <ul className="space-y-1 py-2">
                {likers.map((l, i) => {
                  const name = likerName(l);
                  const avatar = likerAvatar(l);
                  const userId = likerUserId(l);
                  const row = (
                    <>
                      <Avatar className="h-9 w-9">
                        {avatar && <AvatarImage src={avatar} alt="" />}
                        <AvatarFallback aria-hidden="true">
                          {initialsOf(name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="min-w-0 truncate font-medium text-[#3D2817]">
                        {name}
                      </span>
                    </>
                  );
                  return (
                    <li key={l.id ?? `${userId}-${i}`}>
                      {/* Only link when we actually have an id — a missing one
                          used to render href="/profile/undefined". */}
                      {userId ? (
                        <Link
                          href={`/profile/${userId}`}
                          onClick={() => onOpenChange(false)}
                          className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-[#948780]/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#554E49]"
                        >
                          {row}
                        </Link>
                      ) : (
                        <div className="flex items-center gap-3 px-2 py-2">
                          {row}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </InfiniteScroll>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
